import { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import Page from 'src/app/components/page/page'
import { useRequiredProperty } from 'src/app/hooks/context/context.hook'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import NotFoundPage from '../../404.page'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import { useReservationDetailFQuery } from './api/reservation-detail-query.hook'
import { ReservationF, BillF, DepositF, ReservationHeaderTab } from './types/reservation-detail.types'
import { ReservationDetail } from './reservation-detail'
import { Box } from '@vapor/material'
import UnsavedChangesDialog from 'src/app/components/unsaved-changes/unsaved-changes-dialog/unsaved-changes-dialog'
import { ReservationContextProvider } from './context/reservation-context-provider'
import UnsavedChangesTypography from 'src/app/components/unsaved-changes/unsaved-changes-typography/unsaved-changes-typography'
import { HICLoading } from 'hic-components'

export default function ReservationDetailPage() {
  const params = useParams()
  const propertyId = useRequiredProperty().id
  return (
    <>
      {params.reservationId ? ( //
        <ReservationDetailWrapper propertyId={propertyId} reservationId={params.reservationId} appearance={{ appearance: 'PAGE' }} />
      ) : (
        <NotFoundPage />
      )}
    </>
  )
}

export type ReservationDetailDrawerProps = {
  propertyId: string
  reservationId?: string
  reservationTabIndex?: ReservationHeaderTab
  reservedResourceId?: string
  open: boolean
  onClose: () => void
}

export function ReservationDetailDrawer({ propertyId, reservationId, reservedResourceId, reservationTabIndex, open, onClose }: ReservationDetailDrawerProps) {
  const showError = useErrorSnackBar()
  const { t } = useTranslation()

  const [localOpen, setLocalOpen] = useState<boolean>(open)

  useEffect(() => {
    if (!reservationId && localOpen) {
      showError(t('pms.common.missing_parameter'))
    }
  }, [reservationId, localOpen])

  if (open && reservationId) {
    return (
      <ReservationDetailWrapper //
        propertyId={propertyId}
        reservationId={reservationId}
        reservedResourceId={reservedResourceId}
        reservationTabIndex={reservationTabIndex}
        appearance={{
          appearance: 'DRAWER',
          open,
          onClose: () => {
            setLocalOpen(false)
            onClose()
          },
        }}
      />
    )
  } else {
    return <></>
  }
}

//region Types

type ReservationDetailWrapperProps = {
  propertyId: string
  reservationId: string
  reservedResourceId?: string
  reservationTabIndex?: ReservationHeaderTab
  appearance: { appearance: 'PAGE' } | { appearance: 'DRAWER'; open: boolean; onClose: () => void }
}

type ReservationQueryDataType =
  | {
      reservation: ReservationF
      bills: BillF[]
      deposits: DepositF[]
      reservationLink: string | null
    }
  | undefined

//#endregion

function ReservationDetailWrapper({ propertyId, reservationId, reservedResourceId, appearance, reservationTabIndex }: ReservationDetailWrapperProps) {
  const { data, loading, error, refetchReservation } = useReservationDetailFQuery({ propertyId, reservationId })
  const { t } = useTranslation()
  const { xl, lg } = useBreakpoints()
  const showError = useErrorSnackBar()

  const [rrWithUnsavedChanges, setRrWithUnsavedChanges] = useState<string>()
  const [showDialogUnsavedChanges, setShowDialogUnsavedChanges] = useState(false)

  const queryData: ReservationQueryDataType = useMemo(() => {
    if (data !== undefined && data.reservation !== undefined && data.reservation !== null) {
      return {
        reservation: data.reservation,
        bills: data.bills?.slice().sort((a, b) => ((a.open && b.open) || (!a.open && !b.open) ? 0 : a.open ? -1 : 1)) || [],
        deposits: data.deposits || [],
        reservationLink: data.reservationLink ?? null,
      }
    } else {
      return undefined
    }
  }, [JSON.stringify(data)])

  useEffect(() => {
    if (error) {
      showError(error)
      if (appearance.appearance === 'DRAWER' && !loading && data === undefined) {
        appearance.onClose()
      }
    }
  }, [error])

  const onClose = useCallback(() => {
    if (rrWithUnsavedChanges) {
      setShowDialogUnsavedChanges(true)
    }
  }, [rrWithUnsavedChanges])

  if (appearance.appearance === 'PAGE') {
    return loading || data != undefined ? ( //
      <Page title="" hideHeader>
        <Box sx={{ p: 1, pl: { xs: 1, sm: 1, md: 2, lg: 8, xl: 1 } }}>
          <ReservationDetail //
            propertyId={propertyId}
            defaultReservedResourceId={reservedResourceId}
            refetchReservation={refetchReservation}
            queryLoading={loading}
            queryData={queryData}
            onCancel={() => undefined}
            defaultTabIndex={reservationTabIndex}
            unsavedChanges={setRrWithUnsavedChanges}
          />
        </Box>
      </Page>
    ) : (
      <NotFoundPage />
    )
  } else {
    return appearance.open && (loading || data !== undefined) && queryData ? (
      <ReservationContextProvider reservation={queryData.reservation}>
        <FormLayout
          title={t('pms.booking.reservation.detail_page_title')}
          appearance={{ type: 'DRAWER', open: appearance.open, onClose: rrWithUnsavedChanges ? onClose : appearance.onClose, large: true, hideTitle: true }}
          toolbar={
            <FormLayoutDefaultToolbar
              cancelButton={{
                text: t('commons.close'),
                clickHandler: rrWithUnsavedChanges ? onClose : appearance.onClose,
                clickConfirmDialog: (
                  <UnsavedChangesDialog
                    open={showDialogUnsavedChanges}
                    handleCloseDialog={() => {
                      setShowDialogUnsavedChanges(false)
                    }}
                    onCloseAndDiscard={appearance.onClose}
                  />
                ),
              }}
              leftItem={<UnsavedChangesTypography show={Boolean(rrWithUnsavedChanges)} />}
            />
          }
        >
          <ReservationDetail //
            propertyId={propertyId}
            defaultReservedResourceId={reservedResourceId}
            refetchReservation={refetchReservation}
            queryLoading={loading}
            queryData={queryData}
            onCancel={appearance.onClose}
            defaultTabIndex={reservationTabIndex}
            unsavedChanges={setRrWithUnsavedChanges}
          />
        </FormLayout>
      </ReservationContextProvider>
    ) : (
      <>{loading ? <HICLoading /> : undefined}</>
    )
  }
}
